// Investor snapshot: six official yearly figures for a country, each with a
// one-line plain-language reading of what it means for an investor.
// Data: World Bank Open Data (CC BY 4.0), cached by our backend and refreshed
// monthly, so this costs nothing to run.

import { t, lang, glossaryText } from '../i18n/index.js';
import { TERMS } from './glossary.js';

const cache = new Map();

// Order shown in the panel, with the glossary term each one links to.
const ROWS = [
    { id: 'gdp_growth', gloss: 'gdp', fmt: 'pct' },
    { id: 'inflation', gloss: 'inflation', fmt: 'pct' },
    { id: 'unemployment', gloss: 'unemployment', fmt: 'pct' },
    { id: 'gdp_per_capita', gloss: 'gdp', fmt: 'usd' },
    { id: 'current_account', gloss: 'trade-deficit', fmt: 'pctGdp' },
    { id: 'exports', gloss: 'tariff', fmt: 'pctGdp' },
];

/** Plain-language reading of a figure (thresholds are rough, widely used rules of thumb). */
export function interpret(id, v) {
    switch (id) {
        case 'gdp_growth':
            return t(v < 0 ? 'snap.gdp.shrink' : v < 1.5 ? 'snap.gdp.slow' : v < 4 ? 'snap.gdp.moderate' : 'snap.gdp.fast');
        case 'inflation':
            return t(v < 0 ? 'snap.inf.deflation' : v < 3 ? 'snap.inf.target' : v < 6 ? 'snap.inf.above' : 'snap.inf.high');
        case 'unemployment':
            return t(v < 4 ? 'snap.unemp.low' : v < 8 ? 'snap.unemp.moderate' : 'snap.unemp.high');
        case 'gdp_per_capita':
            return t('snap.gdppc.note');
        case 'current_account':
            return t(v >= 0 ? 'snap.ca.surplus' : 'snap.ca.deficit');
        case 'exports':
            return t(v >= 50 ? 'snap.exp.very' : v >= 25 ? 'snap.exp.open' : 'snap.exp.domestic');
        default:
            return '';
    }
}

function format(fmt, v, signed = false) {
    const n = new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (fmt === 'usd') {
        return new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
    }
    const s = `${v > 0 && signed ? '+' : ''}${n.format(v)}%`;
    return fmt === 'pctGdp' ? `${s} ${t('snap.ofGdp')}` : s;
}

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

async function load(iso2) {
    if (cache.has(iso2)) return cache.get(iso2);
    const p = fetch(`/api/countries/${encodeURIComponent(iso2)}/indicators`)
        .then((r) => (r.ok ? r.json() : { data: [] }))
        .then((j) => j.data || [])
        .catch(() => []);
    cache.set(iso2, p);
    return p;
}

/** Fill the #panel-snapshot section for a country (hidden when there is no data). */
export async function renderSnapshot(iso2) {
    const box = document.getElementById('panel-snapshot');
    if (!box) return;
    box.dataset.iso2 = iso2;
    box.hidden = true;
    const data = await load(iso2);
    if (box.dataset.iso2 !== iso2) return; // user opened another country meanwhile
    const byId = Object.fromEntries(data.map((d) => [d.id, d]));
    const rows = ROWS.filter((r) => byId[r.id] && Number.isFinite(byId[r.id].value));
    if (!rows.length) return;

    const list = box.querySelector('.snap-list');
    list.innerHTML = rows
        .map((r) => {
            const d = byId[r.id];
            const g = glossaryText(r.gloss) || TERMS.find((x) => x.id === r.gloss);
            const tip = g ? ` class="gl-term" tabindex="0" data-tip="${esc(`${g.label}: ${g.what}`)}"` : '';
            return `<li class="snap-row">
                <div class="snap-top"><span${tip}>${esc(t(`snap.${r.id}`))}</span>
                <strong class="snap-val">${esc(format(r.fmt, d.value, r.id === 'current_account'))}</strong><span class="snap-year">${d.year}</span></div>
                <p class="snap-why">${esc(interpret(r.id, d.value))}</p></li>`;
        })
        .join('');
    box.hidden = false;
}
